/**
 * titleStamps.js — RC 1.52 Phase 3A.DUP
 *
 * Shared deterministic title-minting helper for SYSTEM-NAMED
 * artefacts (CV Variants, Cover Letters, Application Packs).
 *
 * Format (verbatim, operator-locked 2026-02):
 *   {Readable Label} — YYYY-MM-DD HH:MM:SS.mmm — XXXX
 *
 * Examples:
 *   Senior Commercial Analyst CV — 2026-05-11 14:35:22.842 — A7F3
 *   QatarEnergy Cover Letter — 2026-05-11 14:35:22.842 — 91BC
 *   Application Pack — 2026-05-11 14:35:22.842 — F2A1
 *
 * Why these are SYSTEM names:
 *   Generated artefacts must collide-proof against rapid retries
 *   (same job, same user, sub-second clicks). The backend's
 *   duplicate-title guards remain in place as defence-in-depth —
 *   this helper makes a hit functionally impossible by combining
 *   millisecond timestamps with a 4-char (16-bit) random hex
 *   suffix.
 *
 * Hard rules:
 *   • milliseconds required (no second-only stamps anywhere)
 *   • 4-char uppercase hex suffix required (16 bits of entropy)
 *   • no jobId fragments embedded in the stamp
 *   • Base CV upload names are NOT minted here — they remain
 *     user-controlled.
 */

const _pad = (n, width = 2) => String(n).padStart(width, '0');

/**
 * Millisecond-precision timestamp in local time, "YYYY-MM-DD HH:MM:SS.mmm".
 */
export function _timestampMs(date) {
  const d = date instanceof Date ? date : new Date();
  return (
    `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`
    + ` ${_pad(d.getHours())}:${_pad(d.getMinutes())}:${_pad(d.getSeconds())}`
    + `.${_pad(d.getMilliseconds(), 3)}`
  );
}

/**
 * 4-char uppercase hex suffix (16 bits of entropy). Uses
 * `crypto.getRandomValues` when available; falls back to
 * `Math.random` in SSR / older runtimes.
 */
export function _hex4() {
  let a = 0;
  let b = 0;
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const buf = new Uint8Array(2);
      crypto.getRandomValues(buf);
      a = buf[0];
      b = buf[1];
    } else {
      a = Math.floor(Math.random() * 256);
      b = Math.floor(Math.random() * 256);
    }
  } catch {
    a = Math.floor(Math.random() * 256);
    b = Math.floor(Math.random() * 256);
  }
  return `${_pad(a.toString(16), 2)}${_pad(b.toString(16), 2)}`.toUpperCase();
}

/**
 * Generic helper — composes a stamped title for any readable label.
 *
 *   mintArtefactTitle('Senior Commercial Analyst CV')
 *     -> 'Senior Commercial Analyst CV — 2026-05-11 14:35:22.842 — A7F3'
 */
export function mintArtefactTitle(label) {
  const trimmed = String(label == null ? '' : label).trim();
  const safe = (trimmed || 'Untitled').slice(0, 120);
  return `${safe} — ${_timestampMs()} — ${_hex4()}`;
}

/**
 * CV Variant title.  Role-led ("{Role} CV") with optional caller-
 * supplied suffix so the wizard's options-driven variant suffix
 * (e.g. " · Executive · Strategic · One-page") still composes.
 */
export function mintCvVariantTitle({ role, suffix = '' } = {}) {
  const r = String(role == null ? '' : role).trim().slice(0, 80) || 'Tailored';
  const s = suffix == null ? '' : String(suffix);
  return mintArtefactTitle(`${r} CV${s}`);
}

/**
 * Cover Letter title.  Company-led ("{Company} Cover Letter").
 */
export function mintCoverLetterTitle({ company } = {}) {
  const c = String(company == null ? '' : company).trim().slice(0, 80) || 'Company';
  return mintArtefactTitle(`${c} Cover Letter`);
}

/**
 * Application Pack name.  Fixed readable label "Application Pack" —
 * the timestamp + hex suffix provide collision-proof uniqueness.
 */
export function mintApplicationPackName() {
  return mintArtefactTitle('Application Pack');
}
